import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent, ThemeEvents } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { STORE_SELECTED_EVENT } from '@theme/store-events';
import { fetchCartAttributes, findStoreFromCartAttributes, hasStoreCartAttributes, loadStoreCatalog } from '@theme/store-pricing';
import {
  clearVariantStoreAvailabilityCache,
  computeOptionAvailabilityForPicker,
  getStoreInventoryConfig,
  resolveVariantStoreAvailabilityMap,
} from '@theme/store-inventory';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 * @property {HTMLElement} [loadingIndicator] - Loading spinner shown during section fetch on PDP.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number} */
  #fetchGeneration = 0;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {AbortController | undefined} */
  #storeListenerAbortController;

  /** @type {AbortController | undefined} */
  #storeSyncAbortController;

  /** @type {Map<string, boolean> | null} */
  #variantStoreAvailabilityMap = null;

  /** @type {string | null} */
  #cachedStoreLocationId = null;

  /** @type {boolean} */
  #storeOptionSyncInFlight = false;

  /** @type {number} */
  #sectionFetchInFlight = 0;

  #strikethroughSvgMarkup =
    '<svg viewBox="0 0 100 46" preserveAspectRatio="xMidYMid slice" class="variant-option__strikethrough" aria-hidden="true" data-store-injected="true"><line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke" /><line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke" /></svg>';

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    if (this.#isStoreInventoryPickerEnabled()) {
      this.#storeListenerAbortController = new AbortController();
      const { signal } = this.#storeListenerAbortController;
      document.addEventListener(STORE_SELECTED_EVENT, this.#handleStoreInventoryChange, { signal });
      document.addEventListener(ThemeEvents.variantUpdate, this.#handleStoreVariantUpdate, { signal });
      void this.#syncStoreOptionAvailability();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#storeListenerAbortController?.abort();
    this.#storeSyncAbortController?.abort();
  }

  /**
   * @returns {boolean}
   */
  #isStoreInventoryPickerEnabled() {
    return (
      this.dataset.storeInventoryEnabled === 'true' && this.dataset.templateProductMatch === 'true'
    );
  }

  #handleStoreInventoryChange = () => {
    this.#variantStoreAvailabilityMap = null;
    this.#cachedStoreLocationId = null;
    clearVariantStoreAvailabilityCache();
    void this.#syncStoreOptionAvailability({ reconcileSelection: true });
  };

  /** @param {Event} event */
  #handleStoreVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;
    if (event.detail.data.productId !== this.dataset.productId) return;
    void this.#syncStoreOptionAvailability({ skipFetch: true });
  };

  /**
   * @returns {import('@theme/store-inventory').StoreInventoryPickerVariant[]}
   */
  #getStoreInventoryVariants() {
    const script = this.querySelector('script[data-store-inventory-variants]');
    if (!script?.textContent?.trim()) return [];

    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed.variants) ? parsed.variants : [];
    } catch {
      return [];
    }
  }

  #rebuildRadiosState() {
    this.#radios = [];
    this.#checkedIndices = [];
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input[type="radio"]') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.checked);
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });
  }

  /**
   * @returns {(string | null)[]}
   */
  #getSelectedOptionValuesByPosition() {
    /** @type {(string | null)[]} */
    const selected = [null, null, null];

    this.querySelectorAll('select.variant-option__select').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      const position = Number.parseInt(select.dataset.optionPosition || '', 10);
      if (Number.isNaN(position) || position < 1 || position > 3) return;
      selected[position - 1] = select.value;
    });

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    fieldsets.forEach((fieldset) => {
      const position = Number.parseInt(fieldset.dataset.optionPosition || '', 10);
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (!(checked instanceof HTMLInputElement)) return;
      if (Number.isNaN(position) || position < 1 || position > 3) return;
      selected[position - 1] = checked.value;
    });

    return selected;
  }

  /**
   * @param {HTMLLabelElement} label
   * @param {boolean} show
   */
  #setOptionStrikethrough(label, show) {
    const svg = label.querySelector('.variant-option__strikethrough');

    if (show) {
      if (!svg) {
        label.insertAdjacentHTML('beforeend', this.#strikethroughSvgMarkup);
      } else if (svg instanceof SVGElement) {
        svg.hidden = false;
      }
      return;
    }

    if (svg instanceof SVGElement && svg.dataset.storeInjected === 'true') {
      svg.remove();
    } else if (svg instanceof SVGElement) {
      svg.hidden = true;
    }
  }

  #restoreShopifyOptionAvailability() {
    const soldOutSuffix = Theme.translations?.product_sold_out ?? 'Sold out';
    const unavailableSuffix = Theme.translations?.store_inventory_unavailable ?? 'Unavailable';

    this.querySelectorAll('fieldset input[type="radio"], select.variant-option__select option').forEach(
      (input) => {
        if (!(input instanceof HTMLInputElement || input instanceof HTMLOptionElement)) return;

        const shopifyAvailable = input.dataset.shopifyOptionAvailable === 'true';
        const labelBase = input.dataset.optionLabel || input.value;

        input.dataset.optionAvailable = shopifyAvailable ? 'true' : 'false';

        if (input instanceof HTMLInputElement) {
          if (shopifyAvailable) {
            input.removeAttribute('aria-disabled');
            input.setAttribute('aria-label', labelBase);
          } else {
            input.setAttribute('aria-disabled', 'true');
            input.setAttribute('aria-label', `${labelBase} - ${soldOutSuffix}`);
          }

          const label = input.closest('label');
          if (label instanceof HTMLLabelElement) {
            this.#setOptionStrikethrough(label, !shopifyAvailable);
          }
        } else {
          input.disabled = !shopifyAvailable;
          input.textContent = shopifyAvailable ? labelBase : `${labelBase} - ${unavailableSuffix}`;
        }
      }
    );
  }

  /**
   * @param {Map<string, boolean>} optionAvailability
   */
  #applyStoreOptionAvailability(optionAvailability) {
    const soldOutSuffix = Theme.translations?.product_sold_out ?? 'Sold out';
    const unavailableSuffix = Theme.translations?.store_inventory_unavailable ?? 'Unavailable';

    this.querySelectorAll('fieldset input[type="radio"], select.variant-option__select option').forEach(
      (input) => {
        if (!(input instanceof HTMLInputElement || input instanceof HTMLOptionElement)) return;

        const position = Number.parseInt(input.dataset.optionPosition || '', 10);
        const value = input.value;
        if (Number.isNaN(position) || !value) return;

        const key = `${position}:${value}`;
        if (!optionAvailability.has(key)) return;

        const available = optionAvailability.get(key) === true;
        const labelBase = input.dataset.optionLabel || value;

        input.dataset.optionAvailable = available ? 'true' : 'false';

        if (input instanceof HTMLInputElement) {
          if (available) {
            input.removeAttribute('aria-disabled');
            input.setAttribute('aria-label', labelBase);
          } else {
            input.setAttribute('aria-disabled', 'true');
            input.setAttribute('aria-label', `${labelBase} - ${soldOutSuffix}`);
          }

          const label = input.closest('label');
          if (label instanceof HTMLLabelElement) {
            this.#setOptionStrikethrough(label, !available);
          }
        } else {
          input.disabled = !available;
          input.textContent = available ? labelBase : `${labelBase} - ${unavailableSuffix}`;
        }
      }
    );
  }

  /**
   * @param {{ skipFetch?: boolean; reconcileSelection?: boolean }} [options]
   */
  async #syncStoreOptionAvailability(options = {}) {
    if (!this.#isStoreInventoryPickerEnabled()) return;

    const variants = this.#getStoreInventoryVariants();
    if (!variants.length) return;

    const [cartAttributes, catalog] = await Promise.all([
      fetchCartAttributes(),
      Promise.resolve(loadStoreCatalog()),
    ]);

    if (!hasStoreCartAttributes(cartAttributes)) {
      this.#restoreShopifyOptionAvailability();
      this.#variantStoreAvailabilityMap = null;
      this.#cachedStoreLocationId = null;
      return;
    }

    const store = findStoreFromCartAttributes(catalog, cartAttributes);
    const locationId = store?.shopifyLocationId?.trim() ?? '';

    const needFetch =
      !options.skipFetch ||
      !this.#variantStoreAvailabilityMap ||
      this.#cachedStoreLocationId !== locationId;

    this.#storeSyncAbortController?.abort();
    this.#storeSyncAbortController = new AbortController();
    const { signal } = this.#storeSyncAbortController;

    this.#storeOptionSyncInFlight = true;
    let toggledLoading = false;

    try {
      if (needFetch) {
        toggledLoading = true;
        this.#setVariantPickerLoading(true);
        const { baseUrl, authToken, threshold } = getStoreInventoryConfig(this);
        this.#variantStoreAvailabilityMap = await resolveVariantStoreAvailabilityMap({
          variants,
          cartAttributes,
          catalog,
          baseUrl,
          authToken,
          threshold,
          signal,
        });
        this.#cachedStoreLocationId = locationId;
      }

      if (signal.aborted) return;

      const selectedOptions = this.#getSelectedOptionValuesByPosition();
      const optionAvailability = computeOptionAvailabilityForPicker({
        variants,
        availabilityByVariantId: this.#variantStoreAvailabilityMap ?? new Map(),
        selectedOptions,
      });

      this.#applyStoreOptionAvailability(optionAvailability);

      if (options.reconcileSelection && this.dataset.templateProductMatch === 'true') {
        this.#reconcileVariantSelectionAfterStoreChange(variants);
      }
    } catch (error) {
      if (!(error instanceof Error && error.name === 'AbortError')) {
        console.error(error);
      }
    } finally {
      this.#storeOptionSyncInFlight = false;

      if (toggledLoading && this.#sectionFetchInFlight === 0) {
        this.#setVariantPickerLoading(false);
      }
    }
  }

  /**
   * @param {import('@theme/store-inventory').StoreInventoryPickerVariant[]} variants
   * @param {(string | null)[]} selectedOptions
   * @returns {import('@theme/store-inventory').StoreInventoryPickerVariant | undefined}
   */
  #findVariantForSelectedOptions(variants, selectedOptions) {
    return variants.find((variant) => {
      const optionValues = [variant.option1 ?? null, variant.option2 ?? null, variant.option3 ?? null];

      return optionValues.every((value, index) => {
        const selected = selectedOptions[index];
        if (selected == null || String(selected).trim() === '') return true;
        return String(value ?? '') === String(selected);
      });
    });
  }

  /**
   * @param {import('@theme/store-inventory').StoreInventoryPickerVariant} variant
   * @returns {HTMLInputElement | HTMLSelectElement | undefined}
   */
  #selectVariantInPicker(variant) {
    /** @type {Array<'option1' | 'option2' | 'option3'>} */
    const optionKeys = ['option1', 'option2', 'option3'];
    /** @type {HTMLInputElement | HTMLSelectElement | undefined} */
    let lastTarget;

    optionKeys.forEach((optionKey, index) => {
      const value = variant[optionKey];
      if (value == null || String(value).trim() === '') return;

      const position = index + 1;
      const fieldset = this.querySelector(`fieldset[data-option-position="${String(position)}"]`);
      const radio = fieldset?.querySelector(
        `input[type="radio"][value="${CSS.escape(String(value))}"]`
      );

      if (radio instanceof HTMLInputElement) {
        this.updateSelectedOption(radio);
        lastTarget = radio;
        return;
      }

      const select = this.querySelector(`select.variant-option__select[data-option-position="${String(position)}"]`);
      if (!(select instanceof HTMLSelectElement)) return;

      const option = Array.from(select.options).find((entry) => entry.value === String(value));
      if (!option) return;

      select.value = String(value);
      this.updateSelectedOption(select);
      lastTarget = select;
    });

    return lastTarget;
  }

  /**
   * After store selection changes, auto-switch to the first in-stock variant when the current
   * selection is unavailable at the new store, then fetch updated PDP markup.
   *
   * @param {import('@theme/store-inventory').StoreInventoryPickerVariant[]} variants
   */
  #reconcileVariantSelectionAfterStoreChange(variants) {
    const availabilityMap = this.#variantStoreAvailabilityMap ?? new Map();
    const selectedOptions = this.#getSelectedOptionValuesByPosition();
    const currentVariant = this.#findVariantForSelectedOptions(variants, selectedOptions);
    const currentVariantId = currentVariant ? String(currentVariant.id) : '';
    const currentAvailable = currentVariantId ? availabilityMap.get(currentVariantId) === true : false;

    if (!currentAvailable) {
      const firstAvailable = variants.find((variant) => availabilityMap.get(String(variant.id)) === true);
      if (firstAvailable) {
        const target = this.#selectVariantInPicker(firstAvailable);
        if (target) {
          this.dispatchEvent(
            new VariantSelectedEvent({
              id: target.dataset.optionValueId ?? '',
            })
          );
          this.fetchUpdatedSection(this.buildRequestUrl(target));
        }
        return;
      }
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (
      event.target instanceof HTMLInputElement &&
      event.target.type === 'radio' &&
      event.target.dataset.optionAvailable === 'false' &&
      !this.#storeOptionSyncInFlight
    ) {
      const fieldset = event.target.closest('fieldset');
      const previousChecked = fieldset?.querySelector('input[type="radio"][data-current-checked="true"]');
      if (previousChecked instanceof HTMLInputElement) {
        previousChecked.checked = true;
      }
      event.target.checked = false;
      return;
    }

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * @param {boolean} isLoading
   */
  #setVariantPickerLoading(isLoading) {
    if (this.dataset.templateProductMatch !== 'true') return;

    const { loadingIndicator } = this.refs;

    if (isLoading) {
      this.setAttribute('aria-busy', 'true');
      this.dataset.loading = 'true';

      if (loadingIndicator instanceof HTMLElement) {
        loadingIndicator.hidden = false;
        loadingIndicator.removeAttribute('aria-hidden');
      }
    } else {
      this.removeAttribute('aria-busy');
      delete this.dataset.loading;

      if (loadingIndicator instanceof HTMLElement) {
        loadingIndicator.hidden = true;
        loadingIndicator.setAttribute('aria-hidden', 'true');
      }
    }

    this.dispatchEvent(
      new CustomEvent('variant-picker:loading', {
        bubbles: true,
        detail: { loading: isLoading, productId: this.dataset.productId ?? '' },
      })
    );
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const requestGeneration = ++this.#fetchGeneration;
    const showLoadingIndicator = this.dataset.templateProductMatch === 'true';
    if (showLoadingIndicator) {
      this.#sectionFetchInFlight += 1;
      this.#setVariantPickerLoading(true);
    }

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              requestUrl,
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        if (!showLoadingIndicator) return;

        if (requestGeneration === this.#fetchGeneration) {
          this.#sectionFetchInFlight = Math.max(0, this.#sectionFetchInFlight - 1);
        }

        if (this.#sectionFetchInFlight === 0) {
          this.#setVariantPickerLoading(false);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#rebuildRadiosState();
    this.updateVariantPickerCss();
    void this.#syncStoreOptionAvailability({ skipFetch: true });

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
